import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Switch,
  Table,
  Button,
  Space,
  Modal,
  message,
  Tag,
  Tooltip,
  Statistic,
  Row,
  Col,
  Alert,
} from 'antd';
import {
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';

const { Title, Text } = Typography;
const { confirm } = Modal;

/** Jobs switched on only from Live Ops; they can still be switched off here. */
const LIVE_OPS_ONLY_JOBS: Record<string, string> = {
  lifecycle_push: 'Switched on from Live Ops → Lifecycle Pushes',
};

interface CronJobConfig {
  id: string;
  jobName: string;
  displayName: string;
  description: string;
  schedule: string;
  isEnabled: boolean;
  lastRunAt: string | null;
  lastRunStatus: 'success' | 'failed' | 'running' | 'skipped' | null;
  lastRunMessage: string | null;
  runCount: number;
  failureCount: number;
  createdAt: string;
  updatedAt: string;
}

interface CronJobStats {
  total: number;
  enabled: number;
  disabled: number;
  running: number;
  lastFailures: CronJobConfig[];
}

export const CronJobsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<CronJobConfig[]>([]);
  const [stats, setStats] = useState<CronJobStats | null>(null);
  const [apiAvailable, setApiAvailable] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobsRes, statsRes] = await Promise.allSettled([
        adminApi.getCronJobs(),
        adminApi.getCronJobStats(),
      ]);

      const allNotFound = [jobsRes, statsRes].every(
        (r) =>
          r.status === 'rejected' &&
          (r.reason?.response?.status === 404 || r.reason?.code === 'ERR_NETWORK')
      );

      if (allNotFound) {
        setApiAvailable(false);
        setLoading(false);
        return;
      }

      if (jobsRes.status === 'fulfilled') {
        const data = jobsRes.value.data;
        setJobs(Array.isArray(data) ? data : []);
      }

      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data || null);
      }
    } catch (error) {
      message.error('Failed to load cron job data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleJob = async (jobName: string, currentState: boolean) => {
    const action = currentState ? 'Disable' : 'Enable';
    confirm({
      title: `${action} this job?`,
      icon: <ExclamationCircleOutlined />,
      content: `This will ${action.toLowerCase()} the ${jobName} cron job.`,
      onOk: async () => {
        try {
          await adminApi.toggleCronJob(jobName);
          message.success(`Job ${action.toLowerCase()}d successfully`);
          fetchData();
        } catch (error) {
          message.error(apiErrorMessage(error, `Failed to ${action.toLowerCase()} job`));
        }
      },
    });
  };

  const handleEnableAll = async () => {
    confirm({
      title: 'Enable all jobs?',
      icon: <ExclamationCircleOutlined />,
      content:
        'This will enable all cron jobs including store sync jobs. Lifecycle Push is left as it is (it is switched on from Live Ops).',
      onOk: async () => {
        try {
          await adminApi.enableAllCronJobs();
          message.success('All jobs enabled');
          fetchData();
        } catch (error) {
          message.error('Failed to enable all jobs');
        }
      },
    });
  };

  const handleDisableAll = async () => {
    confirm({
      title: 'Disable all jobs?',
      icon: <ExclamationCircleOutlined />,
      content: 'This will disable all cron jobs. Background processing will stop.',
      okType: 'danger',
      onOk: async () => {
        try {
          await adminApi.disableAllCronJobs();
          message.warning('All jobs disabled');
          fetchData();
        } catch (error) {
          message.error('Failed to disable all jobs');
        }
      },
    });
  };

  const getStatusTag = (status: string | null) => {
    switch (status) {
      case 'success':
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Success
          </Tag>
        );
      case 'failed':
        return (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Failed
          </Tag>
        );
      case 'running':
        return (
          <Tag icon={<SyncOutlined spin />} color="processing">
            Running
          </Tag>
        );
      case 'skipped':
        return (
          <Tag icon={<MinusCircleOutlined />} color="warning">
            Skipped
          </Tag>
        );
      default:
        return (
          <Tag icon={<ClockCircleOutlined />} color="default">
            Never Run
          </Tag>
        );
    }
  };

  const columns = [
    {
      title: 'Job',
      key: 'job',
      render: (_: unknown, record: CronJobConfig) => (
        // A floor for the column: without it, a wide Last Run cell squeezes the
        // job name down to one word per line.
        <div style={{ minWidth: 260 }}>
          <Text strong>{record.displayName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.description}
          </Text>
        </div>
      ),
    },
    {
      title: 'Schedule',
      dataIndex: 'schedule',
      key: 'schedule',
      width: 120,
      render: (schedule: string) => (
        <Tooltip title={`Cron: ${schedule}`}>
          <Tag color="blue">{schedule}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_: unknown, record: CronJobConfig) => getStatusTag(record.lastRunStatus),
    },
    {
      title: 'Last Run',
      dataIndex: 'lastRunAt',
      key: 'lastRunAt',
      width: 240,
      render: (date: string | null, record: CronJobConfig) => (
        <div>
          {date ? (
            <>
              <Text>{new Date(date).toLocaleString()}</Text>
              {record.lastRunMessage && (
                <Tooltip title={record.lastRunMessage}>
                  {/* The table sizes columns to their content, so the one-line
                      message needs a max width to be cut with "…" rather than
                      stretching the column. */}
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: 'block', maxWidth: 220 }}
                    ellipsis
                  >
                    {record.lastRunMessage}
                  </Text>
                </Tooltip>
              )}
            </>
          ) : (
            <Text type="secondary">Never</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Runs',
      key: 'runs',
      width: 100,
      render: (_: unknown, record: CronJobConfig) => (
        <div>
          <Text>{record.runCount} total</Text>
          <br />
          {record.failureCount > 0 && (
            <Text type="danger" style={{ fontSize: 12 }}>
              {record.failureCount} failed
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Enabled',
      key: 'enabled',
      width: 100,
      render: (_: unknown, record: CronJobConfig) => {
        const liveOpsOnly = LIVE_OPS_ONLY_JOBS[record.jobName];
        const toggle = (
          <Switch
            checked={record.isEnabled}
            disabled={Boolean(liveOpsOnly) && !record.isEnabled}
            onChange={() => handleToggleJob(record.jobName, record.isEnabled)}
            checkedChildren={<PlayCircleOutlined />}
            unCheckedChildren={<PauseCircleOutlined />}
          />
        );
        return liveOpsOnly ? <Tooltip title={liveOpsOnly}>{toggle}</Tooltip> : toggle;
      },
    },
  ];

  if (!apiAvailable) {
    return (
      <div>
        <div className="page-header">
          <Title level={3}>Cron Jobs</Title>
          <Text type="secondary">Manage scheduled background jobs</Text>
        </div>
        <Alert
          message="Feature Not Available"
          description="The cron job management API endpoints are not yet implemented on the backend. This feature will be available once the backend support is added."
          type="info"
          showIcon
          style={{ marginTop: 24 }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={3} style={{ marginBottom: 4 }}>
              Cron Jobs
            </Title>
            <Text type="secondary">Manage scheduled background jobs</Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              Refresh
            </Button>
            <Button icon={<PlayCircleOutlined />} onClick={handleEnableAll}>
              Enable All
            </Button>
            <Button icon={<PauseCircleOutlined />} danger onClick={handleDisableAll}>
              Disable All
            </Button>
          </Space>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic title="Total Jobs" value={stats.total} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Enabled"
                value={stats.enabled}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Disabled"
                value={stats.disabled}
                valueStyle={{ color: '#cf1322' }}
                prefix={<PauseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Running"
                value={stats.running}
                valueStyle={{ color: '#1890ff' }}
                prefix={<SyncOutlined spin={stats.running > 0} />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Failed Jobs Alert */}
      {stats && stats.lastFailures.length > 0 && (
        <Alert
          message="Jobs with Recent Failures"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {stats.lastFailures.map((job) => (
                <li key={job.jobName}>
                  <strong>{job.displayName}</strong>: {job.lastRunMessage || 'Unknown error'}
                </li>
              ))}
            </ul>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Jobs Table */}
      <Card>
        <Table
          dataSource={jobs}
          columns={columns}
          rowKey="jobName"
          loading={loading}
          pagination={false}
          size="middle"
          scroll={{ x: 980 }}
        />
      </Card>

      {/* Info */}
      <Card style={{ marginTop: 16 }}>
        <Title level={5}>Job Schedule Reference</Title>
        <Text type="secondary">
          <ul>
            <li>
              <strong>Store Sync - Daily Catalog:</strong> Syncs product catalog from Google Play
              and App Store (disabled by default until connectors are configured)
            </li>
            <li>
              <strong>Store Sync - Validation:</strong> Validates product prices and availability
              (disabled by default)
            </li>
            <li>
              <strong>Cohort - Daily Recalculation:</strong> Updates user cohort assignments based
              on spending patterns
            </li>
            <li>
              <strong>Cohort - Member Count Update:</strong> Updates cached member counts for all
              cohorts
            </li>
            <li>
              <strong>Cohort - Membership Expiration:</strong> Removes expired cohort memberships
            </li>
          </ul>
        </Text>
      </Card>
    </div>
  );
};

export default CronJobsPage;
